import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  GridLegacy as Grid,
  Alert,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import productService from '../services/productService';
import { ProductDownload, LoyaltyLevel } from '../types/productTypes';

interface DownloadsAnalyticsProps {
  productId?: string;
  productName?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const DownloadsAnalytics: React.FC<DownloadsAnalyticsProps> = ({ 
  productId, 
  productName 
}) => {
  const [downloads, setDownloads] = useState<ProductDownload[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [dayFilter, setDayFilter] = useState(30);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (productId) {
        // Load downloads for specific product
        const productDownloads = await productService.getProductDownloads(productId);
        setDownloads(productDownloads);
      } else {
        // Load all downloads and analytics
        const [allDownloads, analyticsData] = await Promise.all([
          productService.getAllDownloads(),
          productService.getDownloadAnalytics(dayFilter)
        ]);
        setDownloads(allDownloads);
        setAnalytics(analyticsData);
      }
    } catch (err) {
      console.error('Error loading download data:', err);
      setError('Failed to load download analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [productId, dayFilter]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getLevelColor = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#666';
    }
  };

  const getLevelIcon = (level: LoyaltyLevel) => {
    switch (level) {
      case 'bronze': return '🥉';
      case 'silver': return '🥈';
      case 'gold': return '🥇';
      default: return '';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {productId ? `Downloads for "${productName}"` : 'Download Analytics'}
      </Typography>

      {!productId && analytics && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">Analytics Summary</Typography>
            <FormControl size="small">
              <InputLabel>Period</InputLabel>
              <Select
                value={dayFilter}
                label="Period"
                onChange={(e) => setDayFilter(e.target.value as number)}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={365}>Last year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {analytics.totalDownloads}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Downloads
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {analytics.uniqueUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Users
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" noWrap>
                    {analytics.topProducts?.[0]?.productName || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Top Product ({analytics.topProducts?.[0]?.downloads || 0} downloads)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {analytics.downloadsByLevel?.map((levelData: any) => (
                      <Chip
                        key={levelData.level}
                        label={`${getLevelIcon(levelData.level)} ${levelData.count}`}
                        size="small"
                        sx={{ bgcolor: `${getLevelColor(levelData.level)}20` }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Downloads by Level
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Recent Downloads" />
          {!productId && <Tab label="Top Products" />}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date & Time</TableCell>
                <TableCell>User</TableCell>
                {!productId && <TableCell>Product</TableCell>}
                <TableCell>Level</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {downloads.slice(0, 100).map((download) => (
                <TableRow key={download.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(download.downloadedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {download.userDisplayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {download.userEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  {!productId && (
                    <TableCell>
                      <Typography variant="body2">
                        {download.productName}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={`${getLevelIcon(download.userLevel)} ${download.userLevel}`}
                      size="small"
                      sx={{ 
                        bgcolor: `${getLevelColor(download.userLevel)}20`,
                        textTransform: 'capitalize'
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {downloads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={productId ? 3 : 4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No downloads recorded yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {downloads.length > 100 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Showing first 100 downloads. Total: {downloads.length}
          </Typography>
        )}
      </TabPanel>

      {!productId && (
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Downloads</TableCell>
                  <TableCell align="right">Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics?.topProducts?.map((product: any, index: number) => (
                  <TableRow key={product.productName}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={index < 3 ? 'bold' : 'normal'}>
                          #{index + 1}
                        </Typography>
                        <Typography variant="body2">
                          {product.productName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {product.downloads}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {analytics.totalDownloads > 0 
                          ? Math.round((product.downloads / analytics.totalDownloads) * 100)
                          : 0}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {(!analytics?.topProducts || analytics.topProducts.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No product data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      )}
    </Box>
  );
}; 